import React, { Fragment } from "react";
import _, { noop } from "lodash";
import dayjs from 'dayjs';

import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { Question, RedoNoteInput, BadAnswerNote } from "../Question";
import { Flex, Text } from "../components";
import {
  validateEmail,
  getRegularExpression,
  validateNRIC,
  getErrorMessage,
} from "../../../assets/js/utils";
import identityConfig from "./identityConfig";

class FormConsumer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorMessageMap: {},
    };
  }

  validate(callback = noop) {
    const { questions } = this.props;
    const errorMessageMap = {};

    for (let i = 0; i < questions.length; i += 1) {
      const question = questions[i];
      const { id: questionId, question_type: questionType } = question;
      const value = question.answer.answer[questionId];

      const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
      if (isEmpty && !question.is_required) continue;
      // In our form stage, the rules is a hash, need to wrap it in an array to keep
      // consistant with the ADP module which enable a better validation method.
      const rules = (() => {
        if (!question.rules) return [];
        if (question.data_validation === null) return []; // WS-14233. Dont validate if data_validation is null. This is a hack to prevent the validation from dirty data.
        if (Array.isArray(question.rules)) return question.rules;
        if (typeof question.rules === "object") return [question.rules];
        return [];
      })();
      const errorMessage = getErrorMessage(value, rules);
      if (errorMessage) {
        errorMessageMap[questionId] = errorMessage;
        continue;
      }

      // Validate for specific question type
      // phone, email, dl, ssn, nric
      if (questionType === "telephone") {
        if (
          !parsePhoneNumberFromString(value) ||
          !parsePhoneNumberFromString(value).isValid()
        ) {
          errorMessageMap[questionId] = "Please enter correct phone number.";
          continue;
        }
      } else if (questionType === "email") {
        if (!validateEmail(value)) {
          errorMessageMap[questionId] = "Please enter a valid email address.";
          continue;
        }
      } else if (questionType === "identity") {
        const { country_code: countryCode, identity_type: identityType } =
          question;
        // deal with drive license
        if (
          ["US"].indexOf(countryCode) !== -1 &&
          ["dl"].indexOf(identityType) !== -1
        ) {
          const selectedStateCode = question.answer.answer.state_code;
          const licenseValue = question.answer.answer[questionId];
          if (!selectedStateCode) {
            errorMessageMap[questionId] = "Please select a state first.";
            continue;
          }
          const regex = getRegularExpression(
            countryCode,
            identityType,
            selectedStateCode,
            identityConfig
          );
          const validator = new RegExp(regex);
          if (!validator.test(licenseValue.trim())) {
            errorMessageMap[questionId] =
              "Please enter a valid driver's license.";
            continue;
          }
        }

        // deal with SSN, NRIC
        if (identityType === "nric") {
          if (!validateNRIC(value.trim())) {
            errorMessageMap[questionId] = "Please enter a valid NRIC number.";
            continue;
          }
        }
        if (["ssn"].indexOf(identityType) !== -1) {
          const regex = getRegularExpression(
            countryCode,
            identityType,
            undefined,
            identityConfig
          );
          const validator = new RegExp(regex);
          if (!validator.test(value)) {
            errorMessageMap[questionId] =
              "Please enter a valid 9-digit SSN number.";
            continue;
          }
        }
      } else if (questionType === "date" && value) {
        // date should be MM/DD/YYYY
        if (!moment(value, "MM/DD/YYYY", true).isValid()) {
          errorMessageMap[questionId] = "Please use correct format MM/DD/YYYY";
          continue;
        }
        if (questionId === "birthday") {
          const today = moment();
          const birthDate = moment(value);
          let age = today.year() - birthDate.year();
          const m = today.month() - birthDate.month();
          if (m < 0 || (m === 0 && today.date() < birthDate.date())) {
            age -= 1;
          }
          if (age < 12 || age > 120) {
            errorMessageMap[questionId] =
              "Age cannot be below 12 or greater than 120.";
            continue;
          }
        }
        // I9 & state tax for date of birth. Date of birth cannot be in the future.
        if (questionId === 'date_of_birth') {
          const today = dayjs();
          const birthDate = dayjs(value);
          if (birthDate.isAfter(today)) {
            errorMessageMap[questionId] = 'Date of birth cannot be in the future.';
            continue;
          }
          // birthday cannot be be more than 120 years in the past
          if (today.diff(birthDate, 'years') >= 120) {
            errorMessageMap[questionId] =
              'Date of birth cannot be more than 120 years in the past.';
            continue;
          }
        }
      }
    }

    if (Object.keys(errorMessageMap).length > 0) {
      this.setState({ errorMessageMap });
      callback(errorMessageMap);
      return errorMessageMap;
    }
    callback();
  }

  updateServerValidations(validations) {
    this.setState({ errorMessageMap: validations });
  }

  render() {
    const { errorMessageMap } = this.state;
    const {
      questions,
      reviewActions,
      editQuestionIds,
      redoQuestionIds,
      badAnswers,
      appendCustomComponent,
      useOriginalQuestionTitle,
    } = this.props;

    const address_country_index = questions.findIndex(
      (question) => question.key == "address_country"
    );
    const address_state_index = questions.findIndex(
      (question) => question.key == "address_state"
    );

    const isInBIStep = address_country_index != -1 && address_state_index != -1;

    if (
      isInBIStep &&
      questions[address_country_index].answer.answer.address_country != ""
    ) {
      questions[address_state_index].disabled = false;
    } else if (isInBIStep) {
      questions[address_state_index].disabled = true;
    }

    if (
      isInBIStep &&
      questions[address_country_index].answer.answer.address_country ==
        "United States"
    ) {
      const { stateOptions } = require("../i9/data");
      const currentStateAnswer =
        questions[
          address_state_index
        ].answer.answer.address_state.toLowerCase();

      const validState = stateOptions.find(
        (state) =>
          state.shortName.toLowerCase() == currentStateAnswer ||
          state.codeValue.toLowerCase() == currentStateAnswer
      );

      if (validState) {
        questions[address_state_index].answer.answer.address_state =
          validState.codeValue;
      } else {
        questions[address_state_index].answer.answer.address_state = "";
      }

      questions[address_state_index].question_type = "dropdown";
      questions[address_state_index].options = stateOptions;
    } else if (isInBIStep) {
      questions[address_state_index].question_type = "short_text";
    }

    const questionComponents = questions.map((question) => {
      const {
        question_type: questionType,
        id: questionId,
        title,
        locked: questionLocked,
      } = question;
      const options = question.options || [];
      question.options = options.filter((op) => op).map((option) => {
        if (option.shortName || option.shortName === '') return option;
        return {
          shortName: useOriginalQuestionTitle ? option : _.startCase(option),
          codeValue: option,
        };
      });
      // value would change base on question type;
      let value = question.answer.answer[questionId];
      const isDriveLicense = isDriveLicenseQuestion(question);
      if (isDriveLicense) value = question.answer.answer;
      if (question.question_type === "attachment") {
        value = question.answer.answer;
      }
      if (question.identity_type === "ssn") {
        value = value.trim();
      }
      if (question.id === "middle_initial") {
        value = value[0];
      }
      if (questionType === "date") {
        // YYYY-MM-DD in system, show MM/DD/YYYY
        const date = moment(
          value,
          ["YYYY-MM-DD", "YYYY/MM/DD", "MM/DD/YYYY", "MMMM D, YYYY"],
          true
        );
        if (date.isValid()) {
          value = date.format("MM/DD/YYYY");
          question.answer.answer[questionId] = value;
        }
      }

      const disabled =
        (reviewActions && editQuestionIds.indexOf(questionId) === -1) ||
        questionLocked ||
        question.disabled;
      const markAsRedo = redoQuestionIds.indexOf(questionId) !== -1;

      return (
        <>
          <Flex column key={`question_container${questionId}`}>
            <Question
              key={questionId}
              title={title}
              name={questionId}
              errorMessage={
                errorMessageMap[questionId] || badAnswers[questionId]
              }
              value={value}
              type={questionType}
              options={question.options}
              placeholder={question.placeholder}
              isRequired={!!question.is_required}
              additionalNote={question.additional_note}
              dateFormat={question.dateFormat || "LL"}
              onChange={({ name, value }) => {
                if (isDriveLicense) {
                  if (name === `${questionId}[state_code]`) {
                    value = {
                      state_code: value,
                    };
                  } else {
                    value = {
                      [questionId]: value,
                    };
                  }
                }
                const errorMessageMap = {
                  ...this.state.errorMessageMap,
                  [question.id]: undefined,
                };
                this.setState({ errorMessageMap });
                this.props.onQuestionChange({ question, value, questions });
              }}
              identityType={question.identity_type}
              hideInput={question.hideInput}
              countryCode={question.country_code}
              content={question.content}
              attachment={question.attachment}
              iframeURL={question.iframe_url}
              // Props related to review mode
              disabled={disabled || question.disabled}
              reviewActions={this.props.reviewActions}
              isEditChecked={editQuestionIds.indexOf(questionId) !== -1}
              isRedoChecked={redoQuestionIds.indexOf(questionId) !== -1}
              onCheckEdit={(checked) => {
                this.props.onEditCheckChange({ question, checked });
              }}
              onCheckRedo={(checked) => {
                this.props.onRedoCheckChange({ question, checked });
              }}
            />
            {markAsRedo && (
              <RedoNoteInput
                value={this.props.redoNotes[question.id]}
                onChange={(e) => {
                  this.props.onRedoNoteChange({
                    question,
                    note: e.target.value,
                  });
                }}
              />
            )}
          </Flex>
          {appendCustomComponent && appendCustomComponent[questionId]}
        </>
      );
    });
    return <Flex column>{questionComponents}</Flex>;
  }
}

export default FormConsumer;

FormConsumer.defaultProps = {
  editQuestionIds: [],
  redoQuestionIds: [],
  badAnswers: {},
};

export const buildFormPayloadFromQuestions = (questions = []) => {
  const formPayload = questions.reduce((acc, question) => {
    const { id: questionId } = question;
    const { answer } = question.answer;

    if (question.question_type === "date") {
      // Vincent: here we convert 'LL' to 'YYYY-MM-DD' but 'LL' format won't be supported in next moment major update...
      // Refer to: http://momentjs.com/guides/#/warnings/js-date/
      const date = moment(answer[questionId]);
      answer[questionId] = date.isValid()
        ? moment(answer[questionId]).format("YYYY-MM-DD")
        : "";
    }
    acc[questionId] = answer;

    return acc;
  }, {});
  return formPayload;
};

// Form related help methods

export const isDriveLicenseQuestion = (question) =>
  question.question_type === "identity" && question.identity_type === "dl";
